// Stage 72 — keyboard shortcut registry singleton.
//
// The direct-keystroke sibling of Stage 71's CommandRegistry. Where the
// command registry is fuzzy-search invocation (Cmd+K palette), this is
// fire-on-keystroke invocation: a single keydown listener at the app shell
// calls match(e) and runs the returned binding's handler.
//
// Three concerns live here:
//   1. Key normalization — a platform-stable canonical form so 'Cmd+K',
//      'Shift+I', '?', 'g p' all compare deterministically regardless of
//      OS, caps-lock, or modifier order.
//   2. Scope priority — modal > page > global; within a scope, last
//      registration wins (and warns).
//   3. Chord support — 'g p' is press-g-then-p within a 1200ms window;
//      Esc cancels mid-chord; a single key that is a chord prefix is
//      forbidden (the chord wins, the single-key registration is refused).
//
// Singleton shape follows the Stage 71 precedent (a module-level
// `export const`, not a static `.instance`).

export type Scope = 'global' | 'page' | 'modal';

export interface ShortcutBinding {
	id: string; // 'global.cmdK' / 'page.cos-ticket-detail.0' / 'modal.palette.esc'
	keys: string; // 'Cmd+K' / 'g p' / 'c' — stored NORMALIZED after register()
	scope: Scope;
	description: string;
	category?: string; // 'Navigation' / 'Action' / 'Help' / 'Palette'
	handler: (e: KeyboardEvent) => void;
	allowInInputs?: boolean; // default false — fire even when focus is in a text field
	preventDefault?: boolean; // default true
}

/** Public chord window. Exposed so tests + docs share one constant. */
export const CHORD_WINDOW_MS = 1200;

const SCOPE_RANK: Record<Scope, number> = { global: 1, page: 2, modal: 3 };

// ---- normalization --------------------------------------------------------
//
// canonical form rules:
//   - One primary modifier family ("Cmd") conflates metaKey (Mac) and
//     ctrlKey (Win/Linux) — the same key wears one name on both platforms.
//   - Alphabetic single letters lowercase, and Shift is tracked as a
//     separate modifier ('Shift+I' -> 'Shift+i') so Shift+letter combos work.
//   - Non-alpha printable keys ('?', '#', '/', '[') keep e.key verbatim and
//     do NOT carry a Shift modifier — the shift is already baked into the
//     glyph (e.g. '?' is Shift+/ on a US layout, but we bind the bare '?').
//   - Named keys map to short canonical names: Esc / Space / Enter / Tab;
//     Arrow* pass through unchanged.
//   - Modifier order is fixed: Cmd, Shift, Alt.

interface KeyParts {
	cmd: boolean;
	shift: boolean;
	alt: boolean;
	base: string;
}

function canonical(parts: KeyParts): string {
	let base = parts.base;
	let shift = parts.shift;
	if (/^[a-zA-Z]$/.test(base)) {
		base = base.toLowerCase();
	} else {
		// Non-alpha base: the shift is part of the glyph; don't double-count it.
		shift = false;
	}
	const mods: string[] = [];
	if (parts.cmd) mods.push('Cmd');
	if (shift) mods.push('Shift');
	if (parts.alt) mods.push('Alt');
	return [...mods, base].join('+');
}

function mapBaseName(raw: string): string {
	const lower = raw.toLowerCase();
	if (lower === 'esc' || lower === 'escape') return 'Esc';
	if (lower === 'space' || lower === 'spacebar' || raw === ' ') return 'Space';
	if (lower === 'enter' || lower === 'return') return 'Enter';
	if (lower === 'tab') return 'Tab';
	return raw; // single char or Arrow* — keep as-is
}

/** Canonical form of a live keyboard event. */
export function normalizeEvent(e: KeyboardEvent): string {
	return canonical({
		cmd: e.metaKey || e.ctrlKey,
		shift: e.shiftKey,
		alt: e.altKey,
		base: mapBaseName(e.key),
	});
}

function normalizeToken(tok: string): string {
	const segs = tok
		.split('+')
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
	if (segs.length === 0) return tok.trim();
	const base = segs[segs.length - 1];
	const mods = segs.slice(0, -1).map((m) => m.toLowerCase());
	const cmd =
		mods.includes('cmd') ||
		mods.includes('command') ||
		mods.includes('ctrl') ||
		mods.includes('control') ||
		mods.includes('meta');
	const shift = mods.includes('shift');
	const alt = mods.includes('alt') || mods.includes('option') || mods.includes('opt');
	return canonical({ cmd, shift, alt, base: mapBaseName(base) });
}

/**
 * Canonical form of a registration string. Handles combos ('Cmd+K'),
 * single keys ('c', '?'), special keys ('Esc'), and chords ('g p' — a
 * space-separated sequence of single keys).
 */
export function normalizeKeys(raw: string): string {
	const trimmed = raw.trim();
	if (/\s/.test(trimmed)) {
		return trimmed
			.split(/\s+/)
			.map((t) => normalizeToken(t))
			.join(' ');
	}
	return normalizeToken(trimmed);
}

// ---- registry -------------------------------------------------------------

export class ShortcutRegistry {
	private bindings = new Map<string, ShortcutBinding>();
	private chordState: { prefix: string; expires: number } | null = null;
	private now: () => number;

	/** `now` is injectable so chord-timing tests don't sleep. */
	constructor(now: () => number = () => Date.now()) {
		this.now = now;
	}

	/**
	 * Register a binding. `keys` is normalized in place. Returns an
	 * unregister fn. A single-key binding that is a chord prefix (or a chord
	 * whose prefix is an existing single key) is REFUSED with a console
	 * warning — the chord wins (D-3).
	 */
	register(binding: ShortcutBinding): () => void {
		const normalized: ShortcutBinding = { ...binding, keys: normalizeKeys(binding.keys) };

		const conflict = this.chordConflict(normalized);
		if (conflict) {
			console.warn(
				`[shortcuts] refusing "${normalized.id}" (${normalized.keys}): collides with chord binding "${conflict.id}" (${conflict.keys}). The chord wins; a single key cannot also be a chord prefix.`,
			);
			return () => {};
		}

		if (this.bindings.has(normalized.id)) {
			console.warn(`[shortcuts] re-registering id "${normalized.id}" (was ${this.bindings.get(normalized.id)!.keys}, now ${normalized.keys})`);
		}
		this.bindings.set(normalized.id, normalized);
		return () => this.unregister(normalized.id);
	}

	unregister(id: string): void {
		this.bindings.delete(id);
	}

	/** All bindings, optionally filtered to one scope. Insertion order. */
	list(scope?: Scope): ShortcutBinding[] {
		const all = [...this.bindings.values()];
		return scope ? all.filter((b) => b.scope === scope) : all;
	}

	/**
	 * Resolve a live keydown to the binding that should fire, or null.
	 * Chord-aware + scope-prioritized. Has the side effect of arming /
	 * clearing chord state — the caller (app shell) must gate input fields
	 * BEFORE calling this (see app-shell _onKeydown) so a chord prefix typed
	 * into a textarea doesn't arm a chord.
	 */
	match(e: KeyboardEvent): ShortcutBinding | null {
		const key = normalizeEvent(e);

		// Mid-chord?
		if (this.chordState) {
			if (this.now() < this.chordState.expires) {
				// Esc cancels the chord and is consumed (no binding fires).
				if (key === 'Esc') {
					this.chordState = null;
					return null;
				}
				const chord = `${this.chordState.prefix} ${key}`;
				this.chordState = null;
				return this.byKey(chord);
			}
			// Window expired — drop it and treat this key as fresh.
			this.chordState = null;
		}

		// Start a chord?
		if (this.isChordPrefix(key)) {
			this.chordState = { prefix: key, expires: this.now() + CHORD_WINDOW_MS };
			return null;
		}

		return this.byKey(key);
	}

	/** Wipe everything (test/teardown helper + clears chord state). */
	reset(): void {
		this.bindings.clear();
		this.chordState = null;
	}

	get size(): number {
		return this.bindings.size;
	}

	/** Exposed for tests — is a chord currently armed? */
	get chordArmed(): boolean {
		return this.chordState !== null && this.now() < this.chordState.expires;
	}

	// ---- internals --------------------------------------------------------

	private byKey(keys: string): ShortcutBinding | null {
		let best: ShortcutBinding | null = null;
		let bestRank = -1;
		for (const b of this.bindings.values()) {
			if (b.keys !== keys) continue;
			const rank = SCOPE_RANK[b.scope];
			// >= so a later registration at equal-or-higher rank wins.
			if (rank >= bestRank) {
				best = b;
				bestRank = rank;
			}
		}
		return best;
	}

	private isChordPrefix(key: string): boolean {
		for (const b of this.bindings.values()) {
			if (b.keys.includes(' ') && b.keys.split(' ')[0] === key) return true;
		}
		return false;
	}

	/** Returns the conflicting binding if `candidate` collides with a chord. */
	private chordConflict(candidate: ShortcutBinding): ShortcutBinding | null {
		const isChord = candidate.keys.includes(' ');
		if (isChord) {
			const prefix = candidate.keys.split(' ')[0];
			for (const b of this.bindings.values()) {
				if (!b.keys.includes(' ') && b.keys === prefix) return b;
			}
		} else {
			for (const b of this.bindings.values()) {
				if (b.keys.includes(' ') && b.keys.split(' ')[0] === candidate.keys) return b;
			}
		}
		return null;
	}
}

/** The one app-wide shortcut registry. */
export const shortcutRegistry = new ShortcutRegistry();
